import { useCallback } from "react";
import { useLanguage } from "../provider/LanguageProvider";

// One PluralRules instance per language for the session.
const pluralRulesCache = new Map();
const pluralCategory = (language, count) => {
  try {
    if (!pluralRulesCache.has(language)) {
      pluralRulesCache.set(language, new Intl.PluralRules(language));
    }
    return pluralRulesCache.get(language).select(count);
  } catch {
    return count === 1 ? "one" : "other";
  }
};

/**
 * Custom hook for translations.
 * Returns:
 *  - t(key, fallback, options): translates a key, falls back to the key itself if not found.
 *    When `options.count` is a number, `key_one` / `key_other` (and for Arabic
 *    `key_zero`, `key_two`, `key_few`, `key_many`) are tried first, then the bare key.
 *  - language: current language code (e.g. 'en', 'ar')
 *  - setLanguage: function to change language
 *  - dir: current direction ('ltr' or 'rtl')
 *  - languages: map of all supported languages
 */
const useTranslation = () => {
  const { translations, language, setLanguage, dir, languages } = useLanguage();

  const t = useCallback(
    (key, fallback, options = {}) => {
      let resolved = translations[key];
      if (options && typeof options.count === "number") {
        const category = pluralCategory(language, options.count);
        resolved = translations[`${key}_${category}`] ?? translations[`${key}_other`] ?? resolved;
      }
      // If translation is missing, prefer explicit fallback, then fall back to the key itself.
      let translation = resolved ?? fallback ?? key;

      // Handle interpolation if options are provided
      if (options && typeof options === "object") {
        Object.keys(options).forEach((optKey) => {
          const value = options[optKey];
          // Replace all occurrences of {{optKey}} with value
          translation = translation.replace(
            new RegExp(`{{${optKey}}}`, "g"),
            value
          );
        });
      }

      return translation;
    },
    [translations, language]
  );

  return { t, language, setLanguage, dir, languages };
};

export default useTranslation;
